import { useState, useRef, useEffect } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

const formatCurrency = (val) => {
  if (val == null || isNaN(val)) return '--';
  return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatNumber = (val) => {
  if (val == null || isNaN(val)) return '--';
  return Number(val).toLocaleString('en-US', { maximumFractionDigits: 2 });
};

// ─── Results Table ────────────────────────────────────────────────────────────

function ResultsTable({ results }) {
  if (!results || results.length === 0) return null;

  // Detect columns from the first few items
  const allKeys = new Set();
  results.slice(0, 20).forEach((r) => Object.keys(r).forEach((k) => allKeys.add(k)));

  // Priority order for columns — show most useful first, hide internal keys
  const hiddenKeys = new Set(['company_id', 'invoice_id#line_item_id', 'company_id#vendor_id',
    'company_id#standardized_name_lower', 'company_id#category', 'line_item_id',
    'raw_extraction', 'raw_item_number', 'standardized_name_lower', 'vendor_id', 'id',
    'uploaded_by', 'source_file_path', 'source', 'processing_status', 'error_message',
    'file_name', 'currency', 'created_at', 'updated_at', 'vendor_snapshot', 'customer_snapshot',
    'raw_extraction', 'vendors']);

  const priorityOrder = [
    // Invoice-level
    'invoice_number', 'vendor_name', 'invoice_date', 'due_date', 'due_status',
    'total_amount', 'days_overdue', 'days_until_due', 'payment_terms',
    'anomaly_type', 'anomaly_reason', 'zscore', 'vendor_avg',
    // Comparison
    'change_type', 'product', 'category', 'newer_invoice', 'newer_date', 'newer_unit_cost',
    'older_invoice', 'older_date', 'older_unit_cost', 'cost_change_pct',
    // Vendor score
    'overall_score', 'price_consistency_score', 'invoice_quality_score', 'payment_terms_score',
    'avg_net_days', 'invoice_count',
    // Line item
    'standardized_name', 'subcategory', 'brand',
    'raw_extended_amount', 'normalized_unit_cost', 'normalized_unit',
    'raw_quantity', 'raw_unit_price',
    // Aggregated / trend
    'period', '_value', '_count',
  ];

  const columns = [];
  // Add priority columns that exist
  for (const key of priorityOrder) {
    if (allKeys.has(key)) columns.push(key);
  }
  // Add remaining columns not in priority or hidden
  for (const key of allKeys) {
    if (!columns.includes(key) && !hiddenKeys.has(key) && !key.startsWith('_')) {
      columns.push(key);
    }
  }
  // Add aggregate columns (start with _)
  for (const key of allKeys) {
    if (key.startsWith('_') && !columns.includes(key)) {
      columns.push(key);
    }
  }

  // Limit to reasonable number of columns
  const displayCols = columns.slice(0, 10);

  const formatHeader = (key) => {
    return key
      .replace(/^_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const DUE_STATUS_STYLES = {
    overdue: 'bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium',
    due_soon: 'bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-medium',
    due_this_month: 'bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium',
    future: 'bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium',
    no_due_date: 'bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full text-xs font-medium',
  };

  const CHANGE_TYPE_STYLES = {
    price_increase: 'bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium',
    new_item: 'bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium',
    price_decrease: 'bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium',
    removed_item: 'bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full text-xs font-medium',
    unchanged: 'bg-zinc-50 text-zinc-400 px-2 py-0.5 rounded-full text-xs font-medium',
  };

  const formatCell = (key, val) => {
    if (val == null || val === '') return '--';

    // Badge cells
    if (key === 'due_status') {
      const label = String(val).replace('_', ' ');
      return <span className={DUE_STATUS_STYLES[val] || DUE_STATUS_STYLES.no_due_date}>{label}</span>;
    }
    if (key === 'change_type') {
      const label = String(val).replace('_', ' ');
      return <span className={CHANGE_TYPE_STYLES[val] || ''}>{label}</span>;
    }
    if (key === 'anomaly_type') {
      const label = String(val).replace(/_/g, ' ');
      return <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium">{label}</span>;
    }

    // Score cells (0–100) — show as number + small bar
    if (key.endsWith('_score') && typeof val === 'number') {
      const color = val >= 70 ? 'bg-green-400' : val >= 40 ? 'bg-amber-400' : 'bg-red-400';
      return (
        <span className="flex items-center gap-1.5">
          <span className="w-16 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <span className={`h-full ${color} rounded-full block`} style={{ width: `${val}%` }} />
          </span>
          <span>{val}</span>
        </span>
      );
    }

    // Percentage change
    if (key === 'cost_change_pct' && typeof val === 'number') {
      const color = val > 0 ? 'text-red-600' : val < 0 ? 'text-green-600' : 'text-zinc-400';
      return <span className={color}>{val > 0 ? '+' : ''}{val}%</span>;
    }

    if (typeof val === 'number') {
      if (key.includes('amount') || key.includes('cost') || key.includes('price') || key === '_value'
        || key === 'total_amount' || key === 'subtotal' || key === 'tax' || key === 'vendor_avg') {
        return formatCurrency(val);
      }
      return formatNumber(val);
    }
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200">
            {displayCols.map((col) => (
              <th key={col} className="text-left py-2 px-3 font-medium text-zinc-500 whitespace-nowrap">
                {formatHeader(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {results.slice(0, 100).map((row, i) => (
            <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
              {displayCols.map((col) => (
                <td key={col} className="py-2 px-3 text-zinc-800 whitespace-nowrap">
                  {formatCell(col, row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {results.length > 100 && (
        <p className="text-xs text-zinc-400 mt-2 px-3">Showing 100 of {results.length} results</p>
      )}
    </div>
  );
}

// ─── Pipeline Visualizer ──────────────────────────────────────────────────────

function PipelineViz({ pipeline }) {
  if (!pipeline?.steps) return null;

  const opColors = {
    filter: 'bg-blue-50 text-blue-700 border-blue-200',
    aggregate: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    sort: 'bg-amber-50 text-amber-700 border-amber-200',
    limit: 'bg-zinc-50 text-zinc-600 border-zinc-200',
    compare: 'bg-purple-50 text-purple-700 border-purple-200',
    trend: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    anomaly: 'bg-red-50 text-red-700 border-red-200',
    compare_invoices: 'bg-orange-50 text-orange-700 border-orange-200',
    due_date_status: 'bg-rose-50 text-rose-700 border-rose-200',
    vendor_score: 'bg-violet-50 text-violet-700 border-violet-200',
  };

  const describeStep = (step) => {
    switch (step.op) {
      case 'filter': {
        const conds = (step.conditions || []).map((c) => `${c.field} ${c.op} ${c.value}`).join(', ');
        return conds || 'all items';
      }
      case 'aggregate': {
        const group = Array.isArray(step.groupBy) ? step.groupBy.join(', ') : step.groupBy;
        const metrics = (step.metrics || []).map((m) => `${m.fn}(${m.field})`).join(', ');
        return `by ${group}${metrics ? ' → ' + metrics : ''}`;
      }
      case 'sort':
        return `${step.field} ${step.dir || 'desc'}`;
      case 'limit':
        return `${step.value} results`;
      case 'compare': {
        const vals = (step.values || []).join(' vs ');
        return `${step.dimension}: ${vals}`;
      }
      case 'trend': {
        const m = step.metric;
        return `${step.granularity || 'month'}ly ${m?.fn || 'sum'}(${m?.field || 'amount'})`;
      }
      case 'anomaly':
        return `${step.metric || 'total_amount'} ≥${step.threshold || 2}σ`;
      case 'compare_invoices':
        return step.vendor_name || 'all vendors';
      case 'due_date_status': {
        const statuses = step.include && step.include.length > 0 ? step.include.join(', ') : 'all';
        return statuses;
      }
      case 'vendor_score': {
        const m = step.metrics && step.metrics.length > 0 ? step.metrics.join(', ') : 'all metrics';
        return m;
      }
      default:
        return JSON.stringify(step);
    }
  };

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {pipeline.steps.map((step, i) => (
        <div key={i} className="flex items-center gap-1">
          {i > 0 && <span className="text-zinc-300">→</span>}
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs border ${opColors[step.op] || 'bg-zinc-50 text-zinc-600 border-zinc-200'}`}>
            <span className="font-semibold">{step.op}</span>
            <span className="opacity-75">{describeStep(step)}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Chat Message ─────────────────────────────────────────────────────────────

function ChatMessage({ message }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end mb-4">
        <div className="bg-zinc-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[80%]">
          <p className="text-sm">{message.content}</p>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="mb-6">
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg className="w-4 h-4 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          {message.answer && (
            <p className="text-sm text-zinc-800 mb-3 leading-relaxed">{message.answer}</p>
          )}

          {message.pipeline && (
            <div className="mb-3">
              <PipelineViz pipeline={message.pipeline} />
            </div>
          )}

          {message.results && message.results.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
              <ResultsTable results={message.results} />
            </div>
          )}

          {message.meta && (
            <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
              <span>{message.meta.totalItems} items scanned</span>
              <span>{message.meta.resultCount} results</span>
              <span>{message.meta.durationMs}ms</span>
            </div>
          )}

          {message.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
              {message.error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────

const EXAMPLE_QUERIES = [
  'What are my top 5 most expensive product categories?',
  'Show me any unusual or anomalous invoices',
  'Which invoices are overdue or due soon?',
  'Compare my last two US Foods invoices',
  'Score my vendors by price consistency',
  'What\'s my monthly spending trend by category?',
  'Which products have the highest unit cost?',
  'Show me all produce items over $500',
];

const AntirView = ({ loaded }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendQuery = async (question) => {
    if (!question.trim() || loading) return;

    const userMsg = { role: 'user', content: question };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await apiFetch('/api/antir/query', {
        method: 'POST',
        body: JSON.stringify({ question }),
      });

      const data = await res.json();

      if (!res.ok) {
        setMessages((prev) => [...prev, { role: 'assistant', error: data.error || 'Query failed' }]);
      } else {
        setMessages((prev) => [...prev, {
          role: 'assistant',
          answer: data.answer,
          results: data.results,
          pipeline: data.pipeline,
          meta: data.meta,
        }]);
      }
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', error: 'Failed to connect to server' }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendQuery(input);
  };

  // Empty state
  if (messages.length === 0) {
    return (
      <div className="max-w-[900px] mx-auto flex flex-col h-[calc(100vh-8rem)]">
        {/* Header */}
        <div className="pt-12 pb-8 text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
              </svg>
            </div>
            <h1 className="text-2xl font-light text-zinc-900">Antir</h1>
          </div>
          <p className="text-zinc-500 text-sm max-w-md mx-auto">
            Ask questions about your invoices and spend data in plain English.
            AI translates your question into a data pipeline and executes it.
          </p>
        </div>

        {/* Example queries */}
        <div className="flex-1 flex items-start justify-center">
          <div className="grid grid-cols-2 gap-2 max-w-lg w-full">
            {EXAMPLE_QUERIES.map((q, i) => (
              <button
                key={i}
                onClick={() => sendQuery(q)}
                className="text-left px-3 py-2.5 rounded-lg border border-zinc-200 text-sm text-zinc-600 hover:bg-zinc-50 hover:border-zinc-300 transition-all"
              >
                {q}
              </button>
            ))}
          </div>
        </div>

        {/* Input bar */}
        <div className="pb-6 pt-4">
          <form onSubmit={handleSubmit} className="relative">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your spend data..."
              className="w-full px-4 py-3 pr-12 rounded-xl border border-zinc-300 text-sm focus:outline-none focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 transition-all"
              autoFocus
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg bg-zinc-900 text-white disabled:opacity-30 hover:bg-zinc-800 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75" />
              </svg>
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Chat state
  return (
    <div className="max-w-[900px] mx-auto flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between py-3 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-zinc-900 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
            </svg>
          </div>
          <span className="text-sm font-medium text-zinc-800">Antir</span>
        </div>
        <button
          onClick={() => setMessages([])}
          className="text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
        >
          Clear chat
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-1">
        {messages.map((msg, i) => (
          <ChatMessage key={i} message={msg} />
        ))}
        {loading && (
          <div className="flex items-start gap-3 mb-4">
            <div className="w-7 h-7 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-zinc-500 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
              </svg>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-xs text-zinc-400">Querying...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input bar */}
      <div className="pb-6 pt-4 border-t border-zinc-100">
        <form onSubmit={handleSubmit} className="relative">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a follow-up question..."
            className="w-full px-4 py-3 pr-12 rounded-xl border border-zinc-300 text-sm focus:outline-none focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 transition-all"
            disabled={loading}
            autoFocus
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg bg-zinc-900 text-white disabled:opacity-30 hover:bg-zinc-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
};

export default AntirView;
